import React, { useEffect, useRef, useState } from 'react';
import { api, fileUrl } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Spinner } from '@/components/common';
import { Upload, X, Film } from 'lucide-react';

const FORMATS = ['video', 'image', 'ugc', 'carousel', 'gif'];
const STATUSES = ['draft', 'live', 'paused', 'winner', 'killed'];
const PLATFORMS = ['Meta', 'TikTok', 'YouTube', 'Pinterest', 'Snapchat', 'Other'];

const empty = { name: '', product_id: '', avatar: '', angle: '', hook: '', format: 'video', status: 'draft', platform: 'Meta', file_url: '', external_link: '', notes: '', spend: 0, impressions: 0, clicks: 0, atc: 0, ic: 0, purchases: 0, revenue: 0 };

export const CreativeDialog = ({ open, onOpenChange, creative, products, defaultProductId, onSaved }) => {
  const isEdit = !!creative?.id;
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(creative ? { ...empty, ...creative } : { ...empty, product_id: defaultProductId || '' });
    }
  }, [open, creative, defaultProductId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setV = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/creatives/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, file_url: data.file_url }));
      toast.success(`Uploaded ${data.filename}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Creative name required'); return; }
    if (!form.product_id) { toast.error('Select a product'); return; }
    setBusy(true);
    try {
      const nums = ['spend', 'impressions', 'clicks', 'atc', 'ic', 'purchases', 'revenue'];
      const payload = { ...form };
      nums.forEach((k) => { payload[k] = payload[k] === '' || payload[k] === null ? 0 : Number(payload[k]); });
      let saved;
      if (isEdit) {
        const { data } = await api.put(`/creatives/${creative.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post('/creatives', payload);
        saved = data;
      }
      toast.success(isEdit ? 'Creative updated' : 'Creative added');
      onOpenChange(false);
      onSaved?.(saved);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const isVideo = form.file_url && /\.(mp4|mov|webm|avi)$/i.test(form.file_url);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Creative' : 'Add Creative'}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Creative name *</Label>
            <Input data-testid="creative-form-name-input" value={form.name} onChange={set('name')} placeholder="e.g. NeckPain-UGC-Hook3" className="mt-1" />
          </div>
          <div>
            <Label>Product *</Label>
            <Select value={form.product_id} onValueChange={setV('product_id')} disabled={isEdit}>
              <SelectTrigger data-testid="creative-form-product-select" className="mt-1"><SelectValue placeholder="Select product" /></SelectTrigger>
              <SelectContent>
                {(products || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Platform</Label>
            <Select value={form.platform} onValueChange={setV('platform')}>
              <SelectTrigger data-testid="creative-form-platform-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Format</Label>
            <Select value={form.format} onValueChange={setV('format')}>
              <SelectTrigger data-testid="creative-form-format-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={setV('status')}>
              <SelectTrigger data-testid="creative-form-status-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Avatar</Label>
            <Input data-testid="creative-form-avatar-input" value={form.avatar} onChange={set('avatar')} placeholder="e.g. Desk worker 30-50" className="mt-1" />
          </div>
          <div>
            <Label>Angle</Label>
            <Input data-testid="creative-form-angle-input" value={form.angle} onChange={set('angle')} placeholder="e.g. Tech-neck relief" className="mt-1" />
          </div>
          <div className="sm:col-span-2">
            <Label>Hook (first 3 seconds)</Label>
            <Input data-testid="creative-form-hook-input" value={form.hook} onChange={set('hook')} placeholder='e.g. "Your neck pain is not from sleeping wrong..."' className="mt-1" />
          </div>
          <div className="sm:col-span-2 rounded-lg border bg-muted/40 p-3">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Creative file / link</Label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*,video/*" onChange={handleUpload} className="hidden" id="creative-file-input" data-testid="creative-form-file-input" />
              <Button data-testid="creative-form-upload-button" type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2">
                {uploading ? <Spinner /> : <Upload className="h-4 w-4" />} Upload file
              </Button>
              {form.file_url && (
                <span className="flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs">
                  <Film className="h-3 w-3" /> file attached
                  <button type="button" onClick={() => setForm((f) => ({ ...f, file_url: '' }))} aria-label="Remove file"><X className="h-3 w-3" /></button>
                </span>
              )}
            </div>
            {form.file_url && (
              <div className="mt-2">
                {isVideo ? (
                  <video src={fileUrl(form.file_url)} controls className="max-h-40 rounded-md" />
                ) : (
                  <img src={fileUrl(form.file_url)} alt="creative preview" className="max-h-40 rounded-md object-contain" />
                )}
              </div>
            )}
            <div className="mt-2">
              <Input data-testid="creative-form-link-input" value={form.external_link} onChange={set('external_link')} placeholder="...or paste an external link (Drive, Dropbox, ad preview)" />
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Performance (from Ads Manager)</Label>
            <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
              {[['spend', 'Spend $'], ['impressions', 'Impressions'], ['clicks', 'Clicks'], ['atc', 'ATC'], ['ic', 'IC'], ['purchases', 'Purchases'], ['revenue', 'Revenue $']].map(([k, label]) => (
                <div key={k}>
                  <Label className="text-xs">{label}</Label>
                  <Input data-testid={`creative-form-${k}-input`} type="number" step="0.01" value={form[k]} onChange={set(k)} className="mt-1 font-mono tabular-nums" />
                </div>
              ))}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">CTR, CPC, CPM, CPA and ROAS are computed automatically.</p>
          </div>
          <div className="sm:col-span-2">
            <Label>Notes</Label>
            <Textarea data-testid="creative-form-notes-input" value={form.notes} onChange={set('notes')} rows={2} className="mt-1" />
          </div>
        </div>
        <Button data-testid="creative-form-submit-button" onClick={submit} disabled={busy} className="w-full">
          {busy && <Spinner className="mr-2" />} {isEdit ? 'Save Changes' : 'Add Creative'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
